import { clearAnalyticsUser } from "./analytics";
import { useAuthStore } from "./store";

/*
 * 탭이 공유하는 인증 세대.
 * server_token, refresh_token, auth-store 와 키가 겹치지 않는다.
 * 같은 탭의 storage 이벤트는 오지 않으므로, 같은 탭 로그아웃은 endSession이 직접 처리한다.
 */

const SHARED_AUTH_GENERATION_KEY = "auth_generation";

/*
 * 메모리 안의 인증 세대.
 * 로그아웃이 시작되거나, 새 로그인 토큰을 저장하기 직전에 1 증가한다.
 * refresh는 이 값과 공유 세대를 시작 시점에 기억하고, 저장 직전에 다시 비교한다.
 * 새로고침하면 메모리 값은 0부터 다시 센다. 공유 세대와 브라우저에 남은 refresh token과는 별개다.
 */

let authGeneration = 0;

export const currentAuthGeneration = () => authGeneration;

export const readSharedAuthGeneration = () => {
    try {
        const raw = localStorage.getItem(SHARED_AUTH_GENERATION_KEY);
        if (!raw) return 0;
        const parsed = Number(raw);
        return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : 0;
    } catch {
        return 0;
    }
};

let lastSeenSharedAuthGeneration = readSharedAuthGeneration();

/*
 * 메모리 세대와 공유 세대를 같은 시점에 올린다.
 * endSession에서는 토큰을 지우기 전에 호출한다.
 */

export const bumpAuthGeneration = () => {
    authGeneration += 1;

    try {
        const next = readSharedAuthGeneration() + 1;
        localStorage.setItem(SHARED_AUTH_GENERATION_KEY, String(next));
        lastSeenSharedAuthGeneration = next;
    } catch (error) {
        console.error("Shared auth generation update failed:", error);
    }
};

/*
 * 다른 탭의 로그아웃을 따라갈 때.
 * 공유 세대는 올리지 않는다. 서버 로그아웃도 호출하지 않는다.
 */

const bumpMemoryAuthGeneration = () => {
    authGeneration += 1;
};

/*
 * 이미 로그아웃 정리가 돌고 있으면 그 promise.
 * 두 번째 호출은 이 정리를 다시 시작하지 않는다.
 */

let activeEndSession: Promise<void> | null = null;

interface EndSessionOptions {
    /*
     * 서버 AuthSession을 refresh token으로 폐기하는 함수.
     * 사용자가 누른 로그아웃만 넘긴다.
     * refresh가 REFRESH_SESSION_INVALID로 죽은 경우에는 넘기지 않는다.
     * 네트워크가 실패해도 로컬 로그아웃은 이미 끝난 뒤다.
     */
    revokeSession?: (refreshToken: string) => Promise<unknown>;
}

const clearLocalAuthState = () => {
    clearAnalyticsUser();

    const authState = useAuthStore.getState();
    authState.setIsLoggedIn(false);
    authState.setIsAdmin(false);
    authState.setIsGuest(false);
};

/*
 * 로그인 세션을 끝낸다.
 * 로컬 토큰과 로그인 상태는 바로 지우고, 서버 세션 폐기는 그 다음에 시도한다.
 * 이 브라우저의 다른 탭은 공유 세대와 토큰 삭제를 storage 이벤트로 본다.
 * 다른 기기의 AuthSession은 여기서 넘긴 refresh token만 폐기한다.
 */

export const endSession = (options: EndSessionOptions = {}): Promise<void> => {
    if (activeEndSession) return activeEndSession;

    bumpAuthGeneration();

    const { revokeSession } = options;
    const refreshToken = revokeSession ? localStorage.getItem("refresh_token") : null;

    localStorage.removeItem("server_token");
    localStorage.removeItem("refresh_token");

    clearLocalAuthState();

    activeEndSession = (async () => {
        if (!revokeSession || !refreshToken) return;

        try {
            await revokeSession(refreshToken);
        } catch (error) {
            console.error("Remote session revoke failed:", error);
        }
    })().finally(() => {
        activeEndSession = null;
    });

    return activeEndSession;
};

/*
 * 다른 탭이 토큰을 지웠거나, 공유 세대가 바뀌며 토큰이 없어진 경우.
 * 메모리 세대만 올려 진행 중인 refresh가 그 토큰을 다시 쓰지 않게 한다.
 */

const followRemoteLogout = () => {
    bumpMemoryAuthGeneration();
    clearLocalAuthState();
};

/*
 * 다른 탭이 로그인하며 토큰을 새로 쓴 경우.
 * 역할은 추측하지 않고, 이미 저장된 auth-store를 한 번 다시 읽는다.
 */

const restorePersistedAuthSession = () => {
    void useAuthStore.persist.rehydrate();
};

let crossTabSyncTimer: number | null = null;

const applyCrossTabAuthChange = () => {
    const serverToken = localStorage.getItem("server_token");
    const refreshToken = localStorage.getItem("refresh_token");
    const sharedGeneration = readSharedAuthGeneration();
    const generationChanged = sharedGeneration !== lastSeenSharedAuthGeneration;

    lastSeenSharedAuthGeneration = sharedGeneration;

    if (!serverToken && !refreshToken) {
        followRemoteLogout();
        return;
    }

    if (generationChanged) {
        restorePersistedAuthSession();
    }
};

const scheduleCrossTabAuthSync = () => {
    if (crossTabSyncTimer !== null) return;

    crossTabSyncTimer = window.setTimeout(() => {
        crossTabSyncTimer = null;
        applyCrossTabAuthChange();
    }, 0);
};

const watchCrossTabAuth = () => {
    if (typeof window === "undefined") return;

    window.addEventListener("storage", (event) => {
        if (event.storageArea !== localStorage) return;

        if (
            event.key !== SHARED_AUTH_GENERATION_KEY &&
            event.key !== "server_token" &&
            event.key !== "refresh_token"
        ) {
            return;
        }

        scheduleCrossTabAuthSync();
    });
};

watchCrossTabAuth();
